import { useEffect, useMemo, useState } from "react";
import { Layers, Loader2, Wand2, Handshake, Minus, Plus, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { useSlip } from "../context/SlipContext";
import { labelFor } from "../lib/sports";
import { ProjectedPick } from "./ProjectedPick";
import { GameLabel } from "./TeamLogo";

const toDecimal = (o) => (o >= 0 ? 1 + o / 100 : 1 + 100 / -o);
const toAmerican = (dec) => (dec >= 2 ? `+${Math.round((dec - 1) * 100)}` : `-${Math.round(100 / (dec - 1))}`);

const suggestCount = (legs) => {
  const strong = legs.filter((l) => l.strong).length;
  const base = strong >= 2 ? Math.min(strong, 6) : Math.min(legs.length, 4);
  return Math.max(1, Math.min(base, legs.length));
};

const MAX_LEGS = 15;

export const ConsensusParlayBuilder = ({ open, onClose }) => {
  const { buildParlay } = useSlip();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ legs: [], sports: [], games: 0 });
  const [step, setStep] = useState(1); // 1 = choose sports, 2 = choose games
  const [selectedSports, setSelectedSports] = useState(() => new Set());
  const [selectedGames, setSelectedGames] = useState(() => new Set());
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setStep(1);
    api
      .get("/consensus-parlay")
      .then((r) => {
        setData(r.data);
        setSelectedSports(new Set());
        setSelectedGames(new Set());
      })
      .catch(() => setData({ legs: [], sports: [], games: 0 }))
      .finally(() => setLoading(false));
  }, [open]);

  // Distinct games available (any sport), for the mix-and-match selector.
  const gamesList = useMemo(() => {
    const seen = new Map();
    for (const l of data.legs) {
      if (!seen.has(l.matchup_id))
        seen.set(l.matchup_id, { matchup_id: l.matchup_id, game_label: l.game_label, sport: l.sport, count: 0 });
      seen.get(l.matchup_id).count += 1;
    }
    return [...seen.values()];
  }, [data.legs]);

  // Count of games & consensus legs per sport (for the sport-picking step).
  const sportStats = useMemo(() => {
    const m = new Map();
    for (const sp of data.sports) m.set(sp, { games: 0, legs: 0 });
    for (const g of gamesList) {
      if (!m.has(g.sport)) m.set(g.sport, { games: 0, legs: 0 });
      const s = m.get(g.sport);
      s.games += 1;
      s.legs += g.count;
    }
    return m;
  }, [data.sports, gamesList]);

  const gamesInSelectedSports = useMemo(
    () => gamesList.filter((g) => selectedSports.has(g.sport)),
    [gamesList, selectedSports]
  );

  const filtered = useMemo(
    () => data.legs.filter((l) => selectedSports.has(l.sport) && selectedGames.has(l.matchup_id)),
    [data.legs, selectedSports, selectedGames]
  );
  // Round-robin the strongest legs across each selected game so the parlay
  // actually spans the sports/games the user chose (e.g. one Dodgers + one Seahawks leg)
  // instead of stacking all legs from a single strong game.
  const ranked = useMemo(() => {
    const byGame = new Map();
    for (const l of filtered) {
      if (!byGame.has(l.matchup_id)) byGame.set(l.matchup_id, []);
      byGame.get(l.matchup_id).push(l);
    }
    const queues = [...byGame.values()];
    const out = [];
    let i = 0;
    while (out.length < filtered.length) {
      const q = queues[i % queues.length];
      if (q.length) out.push(q.shift());
      i += 1;
      if (queues.every((x) => x.length === 0)) break;
    }
    return out;
  }, [filtered]);
  const gamesWithLegs = useMemo(() => new Set(filtered.map((l) => l.matchup_id)).size, [filtered]);
  // Max legs a user can build (up to 10, or fewer if not enough agreements exist).
  const legCap = Math.min(MAX_LEGS, ranked.length);
  // Suggested number of legs: cover each selected game, capped at 10.
  const suggested = useMemo(
    () => Math.max(1, Math.min(legCap, Math.max(suggestCount(ranked), gamesWithLegs))),
    [ranked, gamesWithLegs, legCap]
  );

  // Seed the picked set with the suggested strongest legs when the pool changes.
  useEffect(() => {
    setPicked(new Set(ranked.slice(0, suggested).map((l) => l.prop_id)));
  }, [ranked, suggested]);

  const toggleSport = (sp) =>
    setSelectedSports((s) => {
      const n = new Set(s);
      n.has(sp) ? n.delete(sp) : n.add(sp);
      return n;
    });

  const toggleGame = (mid) =>
    setSelectedGames((s) => {
      const n = new Set(s);
      n.has(mid) ? n.delete(mid) : n.add(mid);
      return n;
    });

  // Move from sports -> games, pre-selecting every game in the chosen sports.
  const goToGames = () => {
    setSelectedGames(new Set(gamesInSelectedSports.map((g) => g.matchup_id)));
    setStep(2);
  };

  // Pick an individual agreement on/off (respect the 10-leg cap).
  const togglePick = (id) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else if (n.size < MAX_LEGS) n.add(id);
      return n;
    });
  // "How many legs" quick control: select the strongest N.
  const pickTopN = (n) => setPicked(new Set(ranked.slice(0, Math.min(n, legCap)).map((l) => l.prop_id)));

  // Parlay = the picked agreements, kept in strongest-first order.
  const chosen = ranked.filter((l) => picked.has(l.prop_id));
  const count = chosen.length;
  const combined = chosen.reduce((a, l) => a * toDecimal(l.odds), 1);
  const payout = (10 * combined).toFixed(2);
  const gamesInvolved = new Set(chosen.map((l) => l.matchup_id)).size;
  const atMax = picked.size >= MAX_LEGS;

  const load = () => {
    buildParlay(
      chosen.map((l) => ({
        prop: {
          id: l.prop_id,
          player_name: l.player_name,
          metric: l.metric,
          line: l.line,
          over_odds: l.side === "OVER" ? l.odds : null,
          under_odds: l.side === "UNDER" ? l.odds : null,
          projection: l.projection,
          bookmaker: l.bookmaker,
          books_agree: l.books_agree,
          books_total: l.books_total,
          team_abbr: l.team_abbr,
          opponent_abbr: l.opponent_abbr,
          game_label: l.game_label,
          sport: l.sport,
        },
        lean: l.side,
      }))
    );
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="consensus-parlay-builder"
        aria-describedby="consensus-builder-desc"
        className="max-w-2xl bg-[#0B0F17] border-[#233248] text-white p-0 overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="relative p-5 border-b border-[#233248] bg-gradient-to-br from-[#10B981]/20 via-[#0B0F17] to-[#00E5FF]/15 overflow-hidden">
          <div className="absolute -right-6 -top-8 h-28 w-28 rounded-full bg-[#10B981]/10 blur-2xl" />
          <div className="relative flex items-start gap-3">
            <div className="h-10 w-10 shrink-0 grid place-items-center rounded-xl bg-gradient-to-br from-[#10B981] to-[#00E5FF] text-[#0B0F17] shadow-lg shadow-[#10B981]/20">
              <Handshake className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-heading font-extrabold text-xl tracking-tight leading-tight">
                Cross-Sport Consensus Parlay
              </DialogTitle>
              <DialogDescription id="consensus-builder-desc" className="text-xs text-slate-400 mt-0.5">
                {step === 1
                  ? "Pick the sports you want — only AI + Market agreement picks, ranked for the best chance to win."
                  : "Pick your games, then set 1–15 legs or hand-pick the exact agreements."}
              </DialogDescription>
            </div>
          </div>
          {/* Step progress */}
          <div className="relative mt-4 flex items-center gap-2 font-data text-[11px]">
            <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-bold ${step === 1 ? "bg-[#10B981] text-[#0B0F17]" : "bg-[#141C2B] text-slate-400 border border-[#233248]"}`}>
              <span className="grid place-items-center h-4 w-4 rounded-full bg-black/20">{step > 1 ? <Check className="h-3 w-3" /> : "1"}</span>
              Sports
            </span>
            <span className={`h-px w-6 ${step === 2 ? "bg-[#10B981]" : "bg-[#233248]"}`} />
            <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-bold ${step === 2 ? "bg-[#00E5FF] text-[#0B0F17]" : "bg-[#141C2B] text-slate-400 border border-[#233248]"}`}>
              <span className="grid place-items-center h-4 w-4 rounded-full bg-black/20">2</span>
              Games &amp; legs
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
          </div>
        ) : data.legs.length === 0 ? (
          <div data-testid="builder-empty" className="py-16 px-6 text-center">
            <Layers className="h-8 w-8 text-slate-600 mx-auto mb-3" />
            <h3 className="font-heading font-bold text-white">No agreement picks yet</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
              Open a few of today's games (across any sport) to load their props, then come back — the builder
              pulls the consensus picks from the games you've opened.
            </p>
          </div>
        ) : step === 1 ? (
          /* ---------- STEP 1: choose sports ---------- */
          <>
            <div className="p-5 overflow-y-auto">
              <div data-testid="builder-sport-select" className="space-y-3">
                <span className="text-sm font-semibold text-slate-200">Choose sports</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {data.sports.map((sp) => {
                    const on = selectedSports.has(sp);
                    const st = sportStats.get(sp) || { games: 0, legs: 0 };
                    return (
                      <button
                        key={sp}
                        data-testid={`builder-sport-${sp}`}
                        onClick={() => toggleSport(sp)}
                        className={`group relative flex flex-col items-start gap-1 rounded-xl px-3.5 py-3 text-left transition-all duration-150 ${
                          on
                            ? "bg-gradient-to-br from-[#10B981] to-[#0ea574] text-[#0B0F17] shadow-lg shadow-[#10B981]/20 ring-1 ring-[#10B981]"
                            : "bg-[#141C2B] border border-[#233248] text-slate-300 hover:border-[#10B981]/60 hover:bg-[#1B273E]"
                        }`}
                      >
                        <span className="flex w-full items-center justify-between font-heading font-bold text-base">
                          {labelFor(sp)}
                          <span className={`grid place-items-center h-5 w-5 rounded-full transition-colors ${on ? "bg-[#0B0F17]/20" : "border border-[#334155] group-hover:border-[#10B981]/60"}`}>
                            {on && <Check className="h-3.5 w-3.5" />}
                          </span>
                        </span>
                        <span className={`font-data text-[11px] ${on ? "text-[#0B0F17]/70" : "text-slate-500"}`}>
                          {st.games} game{st.games !== 1 ? "s" : ""} · {st.legs} picks
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-[#233248] bg-[#141C2B] flex items-center justify-between">
              <span data-testid="builder-sport-summary" className="text-xs text-slate-400 font-data">
                {selectedSports.size} sport{selectedSports.size !== 1 ? "s" : ""} selected
              </span>
              <button
                data-testid="builder-next-games"
                onClick={goToGames}
                disabled={selectedSports.size === 0}
                className="inline-flex items-center gap-2 rounded-xl py-2.5 px-5 font-bold text-[#0B0F17] bg-gradient-to-r from-[#10B981] to-[#00E5FF] hover:brightness-110 disabled:opacity-50"
              >
                Choose games <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          /* ---------- STEP 2: choose games + legs ---------- */
          <>
            <div className="p-4 space-y-4 overflow-y-auto">
              <button
                data-testid="builder-back-sports"
                onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 text-xs font-data text-[#00E5FF] hover:underline"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sports
              </button>

              {/* Game selector — mix props from the chosen sports */}
              <div data-testid="builder-game-select" className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-200">Pick games to combine</span>
                  <div className="flex gap-2.5 text-[11px] font-data">
                    <button
                      data-testid="builder-select-all"
                      onClick={() => setSelectedGames(new Set(gamesInSelectedSports.map((g) => g.matchup_id)))}
                      className="text-[#00E5FF] hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      data-testid="builder-clear"
                      onClick={() => setSelectedGames(new Set())}
                      className="text-slate-400 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                {[...selectedSports].map((sp) => (
                  <div key={sp}>
                    <div className="text-[10px] font-data font-bold uppercase tracking-wide text-[#00E5FF] mb-1">
                      {labelFor(sp)}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {gamesList
                        .filter((g) => g.sport === sp)
                        .map((g) => {
                          const on = selectedGames.has(g.matchup_id);
                          return (
                            <button
                              key={g.matchup_id}
                              data-testid={`builder-game-${g.matchup_id}`}
                              onClick={() => toggleGame(g.matchup_id)}
                              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                                on
                                  ? "bg-[#10B981] text-[#0B0F17]"
                                  : "bg-[#141C2B] border border-[#233248] text-slate-300 hover:border-[#10B981]/50"
                              }`}
                            >
                              {on && <Check className="h-3 w-3" />}
                              {g.game_label || "Game"} <span className="opacity-70">({g.count})</span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>

              {filtered.length === 0 ? (
                <p data-testid="builder-no-games" className="text-sm text-slate-400 text-center py-4">
                  Select at least one game above to build a parlay.
                </p>
              ) : (
                <>
                  {/* How many legs — quick pick the strongest N (up to 15) */}
                  <div className="rounded-xl border border-[#233248] bg-gradient-to-br from-[#141C2B] to-[#101826] p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-sm font-semibold text-slate-200">How many legs</span>
                        <p className="text-[11px] text-slate-500 font-data">Auto-picks the strongest to win</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid="builder-count-dec"
                          onClick={() => pickTopN(Math.max(1, count - 1))}
                          className="h-8 w-8 grid place-items-center rounded-lg bg-[#0B0F17] border border-[#233248] text-white hover:border-[#00E5FF]/50 active:scale-95 transition-transform"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span data-testid="builder-count" className="font-data font-extrabold text-2xl w-8 text-center text-[#00E5FF]">{count}</span>
                        <button
                          data-testid="builder-count-inc"
                          onClick={() => pickTopN(Math.min(legCap, count + 1))}
                          disabled={count >= legCap}
                          className="h-8 w-8 grid place-items-center rounded-lg bg-[#0B0F17] border border-[#233248] text-white hover:border-[#00E5FF]/50 disabled:opacity-40 active:scale-95 transition-transform"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <input
                      data-testid="builder-count-slider"
                      type="range"
                      min={1}
                      max={Math.max(1, legCap)}
                      value={Math.min(Math.max(count, 1), legCap)}
                      onChange={(e) => pickTopN(Number(e.target.value))}
                      className="w-full accent-[#10B981] cursor-pointer"
                    />
                    <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500 font-data">
                      <span>1–{legCap} legs · {ranked.length} agreements available (max 15)</span>
                      <button
                        data-testid="builder-use-suggested"
                        onClick={() => pickTopN(suggested)}
                        className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/10 px-2 py-0.5 text-[#10B981] hover:bg-[#10B981]/20 font-semibold"
                      >
                        ★ Suggested {suggested}
                      </button>
                    </div>
                  </div>

                  {/* Hand-pick the exact agreements */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-200">Or hand-pick the agreements</span>
                    <span data-testid="builder-picked-count" className={`text-[11px] font-data font-bold rounded-full px-2 py-0.5 ${atMax ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#00E5FF]/10 text-[#00E5FF]"}`}>
                      {picked.size}/{MAX_LEGS}{atMax ? " · max" : ""}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 -mt-2">Ranked best odds-to-win first. Tap any agreement to add or drop it.</p>
                  <div className="space-y-1.5" data-testid="builder-legs">
                    {ranked.map((l, i) => {
                      const on = picked.has(l.prop_id);
                      const disabled = !on && atMax;
                      return (
                        <button
                          key={l.prop_id}
                          data-testid={`builder-leg-${i}`}
                          onClick={() => togglePick(l.prop_id)}
                          disabled={disabled}
                          className={`group relative w-full flex items-center justify-between gap-2 rounded-xl border pl-3.5 pr-3 py-2.5 text-left transition-all duration-150 overflow-hidden ${
                            on
                              ? "border-[#10B981]/60 bg-[#10B981]/10 shadow-sm shadow-[#10B981]/10"
                              : disabled
                              ? "border-[#233248] bg-[#0B0F17] opacity-40 cursor-not-allowed"
                              : "border-[#233248] bg-[#0B0F17] hover:border-[#10B981]/40 hover:bg-[#101826]"
                          }`}
                        >
                          {on && <span className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#10B981] to-[#00E5FF]" />}
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span
                              data-testid={`builder-leg-check-${i}`}
                              className={`h-5 w-5 shrink-0 grid place-items-center rounded-md border transition-colors ${
                                on ? "bg-[#10B981] border-[#10B981] text-[#0B0F17]" : "border-[#334155] group-hover:border-[#10B981]/60"
                              }`}
                            >
                              {on && <Check className="h-3.5 w-3.5" />}
                            </span>
                            <div className="min-w-0">
                              <div className="text-white text-sm font-semibold truncate flex items-center">
                                {l.player_name}
                                {l.strong && <span className="ml-1.5 text-[9px] font-data font-bold text-[#10B981] bg-[#10B981]/15 rounded px-1 uppercase">strong</span>}
                              </div>
                              <div className="font-data text-[11px] text-slate-400 truncate">
                                <span className="text-[#00E5FF]">{labelFor(l.sport)}</span> · <GameLabel sport={l.sport} label={l.game_label} />
                              </div>
                              <ProjectedPick
                                testId={`builder-leg-projection-${i}`}
                                projection={l.projection}
                                metric={l.metric}
                                side={l.side}
                                line={l.line}
                                odds={l.odds}
                                book={l.bookmaker}
                                booksAgree={l.books_agree}
                                booksTotal={l.books_total}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 font-data text-xs">
                            <span className="text-[10px] text-[#10B981] bg-[#10B981]/15 rounded px-1">{l.market_pct}%</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer / payout */}
            <div className="p-4 border-t border-[#233248] bg-gradient-to-b from-[#141C2B] to-[#101826]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span data-testid="builder-summary" className="font-data text-sm text-slate-200 font-semibold">
                    {chosen.length} leg{chosen.length !== 1 ? "s" : ""} · {gamesInvolved} game{gamesInvolved !== 1 ? "s" : ""}
                  </span>
                  <p className="text-[10px] text-slate-500 font-data uppercase tracking-wide mt-0.5">AI + Market consensus</p>
                </div>
                <div className="text-right">
                  <div className="font-data font-extrabold text-[#00E5FF] text-2xl leading-none" data-testid="builder-odds">{chosen.length ? toAmerican(combined) : "—"}</div>
                  <div className="text-[11px] text-slate-400 font-data mt-0.5">{chosen.length ? `$10 pays $${payout}` : "pick legs"}</div>
                </div>
              </div>
              <button
                data-testid="builder-load-parlay"
                onClick={load}
                disabled={chosen.length === 0}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 font-bold text-[#0B0F17] bg-gradient-to-r from-[#10B981] to-[#00E5FF] hover:brightness-110 disabled:opacity-50 active:scale-[0.99] transition-transform shadow-lg shadow-[#10B981]/20"
              >
                <Wand2 className="h-4 w-4" /> Load {chosen.length}-leg parlay into slip
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
